import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { withTz } from '@/lib/dateBrt';
import { toast } from 'sonner';
import { Clock, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { getStoredUser } from '@/lib/api';

const GREEN = '#0F6E56';

const EVENT_LABELS = {
  clock_in: { label: 'Entrada', color: '#10b981', short: 'Entrada' },
  lunch_out: { label: 'Saída p/ almoço', color: '#f59e0b', short: 'Almoço →' },
  lunch_in: { label: 'Retorno do almoço', color: '#0ea5e9', short: '← Retorno' },
  clock_out: { label: 'Saída', color: '#6b7280', short: 'Saída' },
};

function formatTimeBrt(iso) {
  if (!iso) return '—';
  // Ensure the string carries a timezone marker; assume UTC when missing.
  const d = new Date(withTz(iso));
  return d.toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Global "Bater ponto" button rendered in the header for ADMIN/CONSULTANT.
 * Shows the next expected event; clicking opens a confirmation modal.
 * The button hides itself for CLIENT users.
 */
export default function PunchClockButton() {
  const [state, setState] = useState(null); // { next_event_type, complete, events, ... }
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [now, setNow] = useState(new Date());

  const user = getStoredUser();
  const role = (user?.role || '').toUpperCase();
  const hidden = role === 'CLIENT' || !user;

  const load = useCallback(async () => {
    if (hidden) { setLoading(false); return; }
    try {
      const { data } = await api.get('/time-clock/today');
      setState(data);
    } catch (e) {
      // silent — surfaces on click if needed
    } finally {
      setLoading(false);
    }
  }, [hidden]);

  useEffect(() => { load(); }, [load]);

  // Tick clock every 30s for the label
  useEffect(() => {
    if (hidden) return undefined;
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, [hidden]);

  if (hidden) return null;

  const nextType = state?.next_event_type;
  const complete = !!state?.complete;
  const nextMeta = nextType ? EVENT_LABELS[nextType] : null;

  const handlePunch = async () => {
    setSubmitting(true);
    try {
      await api.post('/time-clock/punch', { notes: (notes || '').trim() || null });
      toast.success(`${nextMeta?.label} registrado com sucesso.`);
      setNotes('');
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao registrar ponto');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 hover:opacity-90"
        style={{
          borderColor: complete ? '#d1d5db' : (nextMeta?.color || GREEN),
          color: complete ? '#6b7280' : (nextMeta?.color || GREEN),
          backgroundColor: '#fff',
        }}
        aria-label="Bater ponto"
        title={complete ? 'Ponto completo hoje' : `Próximo: ${nextMeta?.label}`}
        data-testid="punch-clock-button"
      >
        <Clock className="w-3.5 h-3.5" />
        <span className="hidden md:inline">
          {complete ? 'Ponto completo' : (nextMeta?.short || 'Bater ponto')}
        </span>
        <span className="md:hidden">Ponto</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); if (!o) setNotes(''); }}>
        <DialogContent className="max-w-md" data-testid="punch-clock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" style={{ color: GREEN }} />
              Bater ponto
            </DialogTitle>
            <DialogDescription>
              {now.toLocaleDateString('pt-BR', {
                timeZone: 'America/Sao_Paulo',
                weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
              })} · Hora atual:{' '}
              <span className="font-medium text-gray-900">
                {now.toLocaleTimeString('pt-BR', {
                  timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-6 flex items-center justify-center text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando...
            </div>
          ) : (
            <div className="space-y-3">
              {/* Today's status */}
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(EVENT_LABELS).map((t) => {
                  const found = state?.events?.find((e) => e.event_type === t);
                  const meta = EVENT_LABELS[t];
                  return (
                    <div
                      key={t}
                      className="border rounded-lg p-2 flex items-center justify-between"
                      style={{ borderColor: found ? meta.color : '#e5e7eb' }}
                      data-testid={`event-slot-${t}`}
                    >
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-500">
                          {meta.label}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatTimeBrt(found?.timestamp)}
                        </p>
                      </div>
                      {found ? (
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: meta.color }}
                        />
                      ) : (
                        <span className="w-2 h-2 rounded-full bg-gray-200 shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>

              {complete ? (
                <p className="text-sm text-gray-600 text-center py-3">
                  ✓ Todos os pontos do dia foram registrados. Bom descanso!
                </p>
              ) : (
                <>
                  <div className="border-t pt-3">
                    <p className="text-xs text-gray-500 mb-1">Próximo evento</p>
                    <p className="text-lg font-semibold" style={{ color: nextMeta?.color }}>
                      {nextMeta?.label}
                    </p>
                  </div>
                  <div>
                    <label htmlFor="pc-notes" className="text-xs font-medium text-gray-700">
                      Observação (opcional)
                    </label>
                    <Textarea
                      id="pc-notes"
                      rows={2}
                      placeholder="Ex: trabalhando em campo, home office..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      maxLength={300}
                      className="text-sm"
                      data-testid="punch-clock-notes"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
                      Cancelar
                    </Button>
                    <Button
                      onClick={handlePunch}
                      disabled={submitting}
                      style={{ backgroundColor: nextMeta?.color || GREEN }}
                      className="text-white hover:opacity-90"
                      data-testid="punch-clock-confirm"
                    >
                      {submitting ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Registrando...</>
                      ) : (
                        <>Registrar {nextMeta?.label}</>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
